import { type ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import { asLanguage } from '@/modules/i18n/languages';

import { buildLanguagePath, parseLanguagePath } from '.';

interface LanguageGateProps {
    children: ReactNode;
}

/**
 * The owner of the URL's language and the only writer of i18next's: every page lives under `/{lang}/`.
 * A URL without the language is replaced by one with it — the remembered language, then the browser's, then
 * the fallback, as i18next's detector has already resolved it — keeping the page path, query and hash; a first
 * segment that is not a language is a page path (`/de/x` → `/en/de/x`, a 404). The language picker only navigates.
 */
const LanguageGate = ({ children }: LanguageGateProps) => {
    const { pathname, search, hash } = useLocation();
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const { lang, rest } = parseLanguagePath(pathname);
    const target = lang === undefined
        ? `${buildLanguagePath(asLanguage(i18n.resolvedLanguage), rest)}${search}${hash}`
        : null;

    useEffect(() => {
        if (target !== null) {
            void navigate(target, { replace: true });
        } else if (lang !== undefined && lang !== i18n.resolvedLanguage) {
            void i18n.changeLanguage(lang);
        }
    }, [target, lang, i18n, navigate]);

    // A URL without the language is never rendered: it is replaced first
    return target === null ? children : null;
};

export default LanguageGate;
